import { combineEpics, Epic, ofType } from 'redux-observable';
import { concat, EMPTY, from, Observable, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';
import { getType } from 'typesafe-actions';

import { ApiMethodExecutingError } from '../../service/Errors';
import { EpicDeps } from '../../service/StoreFactory';
import { State } from '../../typings/state';
import { hideBusy, showBusy } from '../CanvasApp/actions';
import { CanvasAction } from '../common/actions';
import { findItem } from '../common/helpers';

import {
    applyFilter,
    ApplyFilter,
    applyFilterSuccess,
    GetCampaigns,
    getCampaigns,
    getCampaignsSuccess,
    getPreview,
    GetPreview,
    LoadItems,
    loadItems,
    loadItemsSuccess,
    openPreview,
    resetNameToInitial,
    saveName,
    SaveName,
    saveNameSuccess,
    showError,
    Submit,
    submit
} from './actions';
import { createCampaignsList, createCreativesCollection } from './mappers';
import { VideoCreativesCollection } from './typings';

const DEFAULT_OFFSET = 0;

function getSelectedItems(collection: VideoCreativesCollection) {
    return collection.items.filter(item => item.selected);
}

const applyFilterEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(applyFilter)),
        debounceTime(1000),
        switchMap((action: ApplyFilter) => {
            const sharedData = store$.value.canvasEditor.sharedData;

            return from(
                deps.canvasApi.videoCreatives.getCreatives(DEFAULT_OFFSET, action.text, action.showTied, sharedData)
            ).pipe(
                map(result => createCreativesCollection(result)),
                map((collection): CanvasAction => applyFilterSuccess(collection)),
                catchError((error: ApiMethodExecutingError) => of(showError(error.getNativeError().message)))
            );
        })
    );
};

const loadItemsEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(loadItems)),
        switchMap((action: LoadItems) => {
            const sharedData = store$.value.canvasEditor.sharedData;

            return from(
                deps.canvasApi.videoCreatives.getCreatives(action.offset, action.filter, action.showTied, sharedData)
            ).pipe(
                map(result => createCreativesCollection(result)),
                map((collection): CanvasAction => loadItemsSuccess(collection)),
                catchError((error: ApiMethodExecutingError) => of(showError(error.getNativeError().message)))
            );
        })
    );
};

const getCampaignsEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(getCampaigns)),
        switchMap((action: GetCampaigns) => {
            const sharedData = store$.value.canvasEditor.sharedData;
            const tld = store$.value.commonData.tld;

            return from(deps.canvasApi.videoCreatives.getCampaigns(action.id, sharedData)).pipe(
                map(result => createCampaignsList(result, tld)),
                map((campaigns): CanvasAction => getCampaignsSuccess(campaigns)),
                catchError((error: ApiMethodExecutingError) => of(showError(error.getNativeError().message)))
            );
        })
    );
};

const updateItemEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(saveName)),
        switchMap((action: SaveName) => {
            const item = findItem(action.id, store$.value.canvasEditor.canvas.videoCreatives.collection.items);
            const newName = item.nameInputValue ? item.nameInputValue.trim() : '';
            if (!newName || newName === item.name) {
                return of(resetNameToInitial(action.id));
            }

            return from(
                deps.canvasApi.videoCreatives.update(action.id, newName, store$.value.canvasEditor.sharedData)
            ).pipe(
                map((): CanvasAction => saveNameSuccess(action.id)),
                catchError((error: ApiMethodExecutingError) =>
                    of(showError(error.getNativeError().message), resetNameToInitial(action.id))
                )
            );
        })
    );
};

const submitEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(submit)),
        switchMap((action: Submit) => {
            const selectedItems = getSelectedItems(store$.value.canvasEditor.canvas.videoCreatives.collection);
            const sharedData = store$.value.canvasEditor.sharedData;

            return concat(
                of(showBusy()),
                from(deps.canvasApi.videoCreatives.submit(selectedItems, sharedData)).pipe(
                    switchMap(result => {
                        action.creativesSelect(selectedItems.map(item => item.creativeId));

                        return EMPTY;
                    }),
                    catchError((error: ApiMethodExecutingError) =>
                        of(showError(error.getNativeError().message), hideBusy())
                    )
                )
            );
        })
    );
};

const previewEpic: Epic<CanvasAction, CanvasAction, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(getType(getPreview)),
        switchMap((action: GetPreview) => {
            if (!action.videoPreviewData) {
                throw new Error(`PreviewInfo is undefined`);
            }

            return from(
                deps.canvasApi.videoCreatives.getVast(
                    action.id,
                    action.videoPreviewData,
                    store$.value.canvasEditor.sharedData
                )
            ).pipe(
                switchMap(vastInfo => of(openPreview(vastInfo.vast))),
                catchError(
                    (error: ApiMethodExecutingError): Observable<CanvasAction> =>
                        of(showError(error.getNativeError().message))
                )
            );
        })
    );
};

export const videoCreativesEpics = combineEpics(
    applyFilterEpic,
    updateItemEpic,
    getCampaignsEpic,
    loadItemsEpic,
    submitEpic,
    previewEpic
);
